// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Every sound this game can make, declared once.
//
// `sfx.play()` on a name with no patch returns SILENTLY — it does not throw, and it does not warn.
// So a typo'd cue is an INAUDIBLE bug: the suite stays green, the lease is signed, and nothing is
// heard. `tests/source-hygiene.test.ts` greps every `ping(...)` literal in src/ and asserts it
// appears in CUES, and that every CUES entry resolves to a patch some mixer actually knows.
//
// The palette here is deliberately soft. This is a game you leave running while you think, and a
// tower that pings on every lease and every arrival would be unbearable inside a minute — so the
// per-event sounds are quiet and short, and only the things that need a DECISION (a tenant giving
// notice, a car going out of service, a star) are allowed to be loud.

import type { Patch } from '@ben-gy/game-engine/sound';

export const PATCHES: Record<string, Patch> = {
  /** A tool picked up in the build tray. */
  pick: { type: 'triangle', freq: [420, 560], dur: 0.05, gain: 0.16 },
  /** A unit placed. Pitched up by floor, so building tall sounds like building tall. */
  build: { type: 'square', freq: [300, 480], dur: 0.09, gain: 0.18 },
  /** A placement refused. */
  deny: { type: 'square', freq: [220, 150], dur: 0.1, gain: 0.16 },
  /** A unit demolished. */
  raze: { type: 'sawtooth', freq: [260, 90], dur: 0.16, gain: 0.2, noise: true },
  /** A lease signed — the only routinely-good news, so it is the friendliest sound here. */
  lease: { type: 'triangle', freq: [660, 990], dur: 0.13, gain: 0.17 },
  /** A tenant gives notice. Loud on purpose: it is the one event you must act on. */
  notice: { type: 'sawtooth', freq: [420, 190], dur: 0.34, gain: 0.26 },
  /** A car's doors at a stop. Very quiet — it fires dozens of times a day. */
  ding: { type: 'sine', freq: [880, 880], dur: 0.045, gain: 0.07 },
  /** A car taken out of service by wear. */
  fault: { type: 'sawtooth', freq: [300, 120], dur: 0.4, gain: 0.28, noise: true },
  /** A car returned to service. */
  mend: { type: 'triangle', freq: [440, 880], dur: 0.2, gain: 0.2 },
  /** The day rolls over and the books are done. */
  daybreak: { type: 'sine', freq: [330, 495], dur: 0.26, gain: 0.15 },
  /** A star gained. */
  star: { type: 'triangle', freq: [660, 1320], dur: 0.5, gain: 0.28 },
  /** A star lost. The mirror of `star`, falling. */
  demote: { type: 'triangle', freq: [880, 330], dur: 0.45, gain: 0.24 },
  /** A tutorial step completed. */
  step: { type: 'triangle', freq: [560, 840], dur: 0.11, gain: 0.18 },
  /** The run ends well. */
  award: { type: 'triangle', freq: [520, 1560], dur: 0.7, gain: 0.3 },
  /** The run ends badly. */
  bust: { type: 'sawtooth', freq: [340, 80], dur: 0.7, gain: 0.3 },
};

/**
 * The closed roster. Anything played must be in here; anything in here must resolve to a patch
 * above or to one of the engine's built-ins.
 */
export const CUES: readonly string[] = [
  ...Object.keys(PATCHES),
  // engine built-ins this game leans on
  'select',
  'blip',
];
